import React, { useEffect, useState, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ImageUploader from '../components/ImageUploader';
import { PasswordTab } from './CustomerProfile';
import {
  LogOut, Plus, Edit3, Trash2, Upload, Download, Package, Folder, Mail, FileSpreadsheet, X, Eye, ExternalLink, LayoutGrid, RotateCcw, ShoppingCart, ChevronRight, ChevronDown as ChevDown, Filter, Users, Shield, KeyRound, AlertCircle, CheckCircle2, Clock, Copy, FileText, Building2,
} from 'lucide-react';

const TABS = [
  { id: 'tiles', label: 'Home Page', icon: LayoutGrid },
  { id: 'products', label: 'Prodotti', icon: Package },
  { id: 'categories', label: 'Categorie', icon: Folder },
  { id: 'import', label: 'Import Excel', icon: FileSpreadsheet },
  { id: 'customers', label: 'Clienti', icon: Users },
  { id: 'cart-quotes', label: 'Richieste Carrello', icon: ShoppingCart },
  { id: 'quotes', label: 'Preventivi (singoli)', icon: Mail },
  { id: 'contacts', label: 'Contatti', icon: Mail },
  { id: 'opening-hours', label: 'Orari Apertura', icon: Clock },
  { id: 'site-info', label: 'Contatti & Dati Fiscali', icon: Building2 },
  { id: 'admins', label: 'Admin', icon: Shield },
  { id: 'password', label: 'Cambio Password', icon: KeyRound },
];

const AdminDashboard = () => {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState('products');

  if (loading) return <div className="p-10 text-neutral-500">Caricamento...</div>;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black brand-text text-neutral-900">Pannello Admin</h1>
          <p className="text-sm text-neutral-500">
            Benvenuto, <strong>{user?.name || user?.email}</strong>
            {user?.is_primary && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">Admin Principale</span>}
          </p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="flex gap-1 border-b border-neutral-200 mb-6 overflow-x-auto">
        {TABS.map((tt) => {
          const Icon = tt.icon;
          return (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                tab === tt.id ? 'border-[#d62828] text-[#d62828]' : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <Icon size={16} /> {tt.label}
            </button>
          );
        })}
      </div>

      {tab === 'tiles' && <HomeTilesTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'import' && <ImportTab />}
      {tab === 'customers' && <CustomersTab />}
      {tab === 'cart-quotes' && <CartQuotesTab />}
      {tab === 'quotes' && <QuotesTab />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'opening-hours' && <OpeningHoursTab />}
      {tab === 'site-info' && <SiteInfoTab />}
      {tab === 'admins' && <AdminsTab currentAdmin={user} />}
      {tab === 'password' && <PasswordTab scope="admin" />}
    </div>
  );
};

// =================== ADMINS MANAGEMENT ===================
const AdminsTab = ({ currentAdmin }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const isPrimary = currentAdmin?.is_primary;

  const load = async () => {
    try {
      const r = await api.get('/admin/users');
      setItems(r.data);
    } catch (e) {
      console.warn('[admins] failed to load admin list:', e);
    }
  };
  useEffect(() => { load(); }, []);

  const createAdmin = async (data) => {
    try {
      await api.post('/admin/users', data);
      toast({ title: 'Admin creato' });
      setCreating(false);
      load();
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const updateAdmin = async (id, data) => {
    try {
      await api.put(`/admin/users/${id}`, data);
      toast({ title: 'Admin aggiornato' });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const delAdmin = async (id) => {
    if (!window.confirm('Eliminare questo admin?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast({ title: 'Admin eliminato' });
      load();
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Gestione Admin ({items.length})</h2>
        {isPrimary && (
          <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">
            <Plus size={16} /> Nuovo Admin
          </button>
        )}
      </div>
      {!isPrimary && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" /> Solo l'admin principale può creare/eliminare altri admin. Puoi solo visualizzare la lista.
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Ruolo</th>
              <th className="px-4 py-3">Creato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-neutral-100">
                <td className="px-4 py-3 font-medium">{a.name || '—'}</td>
                <td className="px-4 py-3 text-neutral-600">{a.email}</td>
                <td className="px-4 py-3">
                  {a.is_primary
                    ? <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">Principale</span>
                    : <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded">Standard</span>}
                </td>
                <td className="px-4 py-3 text-xs text-neutral-500">{new Date(a.created_at).toLocaleDateString('it-IT')}</td>
                <td className="px-4 py-3 text-right">
                  {isPrimary && !a.is_primary && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(a)} className="p-2 hover:bg-neutral-100 rounded"><Edit3 size={14} /></button>
                      <button onClick={() => delAdmin(a.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal onClose={() => setCreating(false)} title="Nuovo Admin">
          <AdminForm onSubmit={createAdmin} onCancel={() => setCreating(false)} mode="create" />
        </Modal>
      )}
      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Modifica Admin: ${editing.email}`}>
          <AdminForm initial={editing} onSubmit={(d) => updateAdmin(editing.id, d)} onCancel={() => setEditing(null)} mode="edit" />
        </Modal>
      )}
    </div>
  );
};

const AdminForm = ({ initial = {}, onSubmit, onCancel, mode = 'create' }) => {
  const [d, setD] = useState({
    name: initial.name || '',
    email: initial.email || '',
    password: '',
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); const payload = mode === 'create' ? d : { name: d.name, email: d.email }; onSubmit(payload); }} className="space-y-3">
      <Input label="Nome *" required value={d.name} onChange={(v) => setD({ ...d, name: v })} />
      <Input label="Email *" type="email" required value={d.email} onChange={(v) => setD({ ...d, email: v })} />
      {mode === 'create' && (
        <Input label="Password * (min 6 caratteri)" type="password" required value={d.password} onChange={(v) => setD({ ...d, password: v })} />
      )}
      {mode === 'edit' && (
        <p className="text-xs text-neutral-500">Per cambiare password, l'admin deve effettuare il "Cambio password" dal proprio account.</p>
      )}
      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-5 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">{mode === 'create' ? 'Crea Admin' : 'Salva'}</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Annulla</button>
      </div>
    </form>
  );
};

// =================== HOME TILES ===================
const HomeTilesTab = () => {
  const [tiles, setTiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    const r = await api.get('/home-tiles');
    setTiles(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (slot, data) => {
    try {
      await api.put(`/home-tiles/${slot}`, data);
      setEditing(null);
      load();
      toast({ title: 'Riquadro aggiornato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const reset = async () => {
    if (!window.confirm('Resettare tutti i 14 riquadri ai valori di default?')) return;
    await api.post('/admin/reset-home-tiles');
    load();
    toast({ title: 'Riquadri resettati' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Home Page – Riquadri ({tiles.length}/14)</h2>
        <button onClick={reset} className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50">
          <RotateCcw size={14} /> Reset Default
        </button>
      </div>
      <p className="text-sm text-neutral-600 mb-4">
        Personalizza i 14 riquadri della home page. Clicca su "Modifica" per cambiare titolo, immagine, colori e link di destinazione.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((tile) => (
          <div key={tile.slot} className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <div className="h-32 relative" style={{ backgroundColor: tile.bg_color }}>
              {tile.image_url && (
                <img src={tile.image_url} alt="" className="absolute right-2 top-1/2 -translate-y-1/2 h-[80%] object-contain opacity-90" />
              )}
              <div className="absolute inset-0 p-3 flex flex-col justify-center" style={{ color: tile.text_color }}>
                {tile.badge && <span className="text-[10px] font-bold uppercase tracking-wider opacity-80 truncate">{tile.badge}</span>}
                <span className="text-sm font-black brand-text leading-tight line-clamp-2">{tile.title}</span>
                {tile.subtitle && <span className="text-xs opacity-80 line-clamp-1 mt-1">{tile.subtitle}</span>}
              </div>
              <span className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-0.5 rounded">#{tile.slot}</span>
              {!tile.visible && <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded">Nascosto</span>}
            </div>
            <div className="p-3 flex items-center justify-between">
              <div className="text-xs text-neutral-500 truncate flex-1 mr-2">{tile.layout_key} → {tile.link_url}</div>
              <button onClick={() => setEditing(tile)} className="px-3 py-1.5 bg-[#d62828] text-white text-xs font-bold rounded hover:bg-[#b41f1f] flex items-center gap-1">
                <Edit3 size={12} /> Modifica
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Modifica Riquadro #${editing.slot} (${editing.layout_key})`} large>
          <TileForm tile={editing} onSubmit={(data) => save(editing.slot, data)} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
};

const TileForm = ({ tile, onSubmit, onCancel }) => {
  const [d, setD] = useState({
    title: tile.title || '',
    subtitle: tile.subtitle || '',
    badge: tile.badge || '',
    image_url: tile.image_url || '',
    bg_color: tile.bg_color || '#ffffff',
    text_color: tile.text_color || '#000000',
    link_url: tile.link_url || '/noleggio',
    image_position: tile.image_position || 'right',
    visible: tile.visible !== false,
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(d); }} className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Titolo" value={d.title} onChange={(v) => setD({ ...d, title: v })} />
        <Input label="Sottotitolo" value={d.subtitle} onChange={(v) => setD({ ...d, subtitle: v })} />
        <Input label="Badge / etichetta" value={d.badge} onChange={(v) => setD({ ...d, badge: v })} />
        <Input label="URL Immagine" value={d.image_url} onChange={(v) => setD({ ...d, image_url: v })} />
        <Input label="Link (es. /categoria/microfoni)" value={d.link_url} onChange={(v) => setD({ ...d, link_url: v })} />
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Posizione immagine</label>
          <select value={d.image_position} onChange={(e) => setD({ ...d, image_position: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded">
            <option value="right">Destra</option>
            <option value="left">Sinistra</option>
            <option value="center">Centro</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Colore Sfondo</label>
          <div className="flex gap-2">
            <input type="color" value={d.bg_color} onChange={(e) => setD({ ...d, bg_color: e.target.value })} className="h-10 w-12 border border-neutral-300 rounded cursor-pointer" />
            <input type="text" value={d.bg_color} onChange={(e) => setD({ ...d, bg_color: e.target.value })} className="flex-1 px-3 py-2 border border-neutral-300 rounded font-mono text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Colore Testo</label>
          <div className="flex gap-2">
            <input type="color" value={d.text_color} onChange={(e) => setD({ ...d, text_color: e.target.value })} className="h-10 w-12 border border-neutral-300 rounded cursor-pointer" />
            <input type="text" value={d.text_color} onChange={(e) => setD({ ...d, text_color: e.target.value })} className="flex-1 px-3 py-2 border border-neutral-300 rounded font-mono text-sm" />
          </div>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={d.visible} onChange={(e) => setD({ ...d, visible: e.target.checked })} />
        <span>Mostra in home page</span>
      </label>

      {/* Anteprima */}
      <div>
        <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Anteprima</p>
        <div className="h-32 rounded-lg relative overflow-hidden border border-neutral-200" style={{ backgroundColor: d.bg_color }}>
          {d.image_url && (
            <img src={d.image_url} alt="" className={`absolute ${d.image_position === 'left' ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 h-[80%] object-contain`} />
          )}
          <div className={`absolute inset-0 p-3 flex flex-col justify-center ${d.image_position === 'left' ? 'pl-[40%]' : 'pr-[40%]'}`} style={{ color: d.text_color }}>
            {d.badge && <span className="text-xs font-bold uppercase tracking-wider opacity-80">{d.badge}</span>}
            <span className="text-lg font-black brand-text leading-tight">{d.title}</span>
            {d.subtitle && <span className="text-xs opacity-80 mt-1">{d.subtitle}</span>}
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-5 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">Salva</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Annulla</button>
      </div>
    </form>
  );
};

// =================== CATEGORIES ===================
const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    const r = await api.get('/admin/categories');
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing.id) await api.put(`/categories/${editing.id}`, data);
      else await api.post('/categories', data);
      setEditing(null); load();
      toast({ title: 'Salvato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore salvataggio', variant: 'destructive' });
    }
  };
  const toggleHidden = async (cat) => {
    try {
      const payload = {
        name: cat.name, slug: cat.slug, description: cat.description || '',
        image_url: cat.image_url || '', parent_id: cat.parent_id || null,
        order: cat.order || 0, hidden: !cat.hidden,
      };
      await api.put(`/categories/${cat.id}`, payload);
      load();
      toast({ title: !cat.hidden ? 'Categoria nascosta' : 'Categoria visibile', description: !cat.hidden ? 'Non appare sul sito pubblico.' : 'Visibile ai clienti.' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminare questa categoria?')) return;
    try {
      await api.delete(`/categories/${id}`);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail || {};
        const msg = `Categoria non vuota:\n- ${d.children || 0} sottocategorie\n- ${d.products || 0} prodotti\n\nProcedere comunque?\nLe sottocategorie verranno spostate al livello superiore.\nI prodotti verranno scollegati (rimarranno nel catalogo ma senza categoria).`;
        if (window.confirm(msg)) {
          try {
            await api.delete(`/categories/${id}?force=true`);
            toast({ title: 'Categoria eliminata', description: `Riassegnate ${d.children} sottocategorie e scollegati ${d.products} prodotti.` });
            load();
          } catch (e2) {
            toast({ title: 'Errore', description: e2.response?.data?.detail || 'Errore', variant: 'destructive' });
          }
        }
      } else {
        toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Categorie ({items.length})</h2>
        <button onClick={() => setEditing({ name: '', slug: '', description: '', image_url: '', order: 0, parent_id: null, hidden: false })} className="flex items-center gap-2 px-4 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">
          <Plus size={16} /> Nuova
        </button>
      </div>
      <p className="text-sm text-neutral-600 mb-4">Le categorie supportano fino a 3 livelli (es. <strong>Audio &gt; Microfoni &gt; Shotgun</strong>). Crea prima la categoria principale, poi le sotto-categorie selezionando il padre.</p>

      <CategoryTree items={items} onEdit={(c) => setEditing(c)} onDelete={del} onToggleHidden={toggleHidden} onAddChild={(parent) => setEditing({ name: '', slug: '', description: '', image_url: '', order: 0, parent_id: parent.id, hidden: false })} />

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Modifica Categoria' : 'Nuova Categoria'}>
          <CategoryForm initial={editing} categories={items} onSubmit={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
};

const CategoryTree = ({ items, onEdit, onDelete, onToggleHidden, onAddChild }) => {
  const tops = items.filter((c) => !c.parent_id);
  const childrenOf = (id) => items.filter((c) => c.parent_id === id);

  const renderNode = (cat, level = 0) => {
    const children = childrenOf(cat.id);
    return (
      <div key={cat.id} className={`border-b border-neutral-100 last:border-b-0 ${cat.hidden ? 'bg-neutral-50/60 opacity-70' : ''}`}>
        <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-neutral-50" style={{ paddingLeft: `${12 + level * 24}px` }}>
          {level > 0 && <ChevronRight size={14} className="text-neutral-400" />}
          {cat.image_url && <img src={cat.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold truncate ${cat.hidden ? 'text-neutral-500 line-through' : 'text-neutral-800'}`}>{cat.name}</span>
              <span className="text-xs text-neutral-400 truncate">/{cat.slug}</span>
              {level === 0 && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">L1</span>}
              {level === 1 && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">L2</span>}
              {level === 2 && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">L3</span>}
              {cat.hidden && <span className="text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5" data-testid="hidden-badge">🚧 NASCOSTA</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <label className="flex items-center gap-1 text-xs px-2 py-1 cursor-pointer hover:bg-neutral-100 rounded" title="Nascondi/mostra sul sito pubblico">
              <input
                type="checkbox"
                checked={!!cat.hidden}
                onChange={() => onToggleHidden(cat)}
                className="accent-[#d62828]"
                data-testid={`toggle-hidden-${cat.slug}`}
              />
              <span className="text-neutral-600 select-none">Nascondi</span>
            </label>
            {level < 2 && (
              <button onClick={() => onAddChild(cat)} className="p-1.5 text-xs text-[#d62828] hover:bg-red-50 rounded font-bold flex items-center gap-1" title="Aggiungi sottocategoria">
                <Plus size={12} /> Sotto
              </button>
            )}
            <button onClick={() => onEdit(cat)} className="p-2 hover:bg-neutral-200 rounded"><Edit3 size={14} /></button>
            <button onClick={() => onDelete(cat.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
          </div>
        </div>
        {children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  if (tops.length === 0) return <div className="p-6 text-center text-neutral-500 bg-white border border-neutral-200 rounded-lg">Nessuna categoria. Clicca &quot;Nuova&quot; per crearne una.</div>;

  return <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">{tops.map((c) => renderNode(c, 0))}</div>;
};

const CategoryForm = ({ initial, categories, onSubmit, onCancel }) => {
  const [data, setData] = useState({
    name: initial.name || '', slug: initial.slug || '', description: initial.description || '',
    image_url: initial.image_url || '', order: initial.order || 0,
    parent_id: initial.parent_id || null,
    hidden: !!initial.hidden,
  });

  // Compute level for the chosen parent (so we can prevent 4-level)
  const getLevel = (catId) => {
    if (!catId) return 0;
    const cat = (categories || []).find((c) => c.id === catId);
    if (!cat) return 0;
    return 1 + getLevel(cat.parent_id);
  };

  const parentLevel = getLevel(data.parent_id);
  const allowedParents = (categories || []).filter((c) => c.id !== initial.id && getLevel(c.id) < 2);

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(data); }} className="space-y-3">
      <Input label="Nome *" required value={data.name} onChange={(v) => setData({ ...data, name: v })} />
      <Input label="Slug (auto se vuoto)" value={data.slug} onChange={(v) => setData({ ...data, slug: v })} />
      <div>
        <label className="block text-sm font-bold text-neutral-700 mb-1">Categoria padre (lascia vuoto per livello 1)</label>
        <select value={data.parent_id || ''} onChange={(e) => setData({ ...data, parent_id: e.target.value || null })} className="w-full px-3 py-2 border border-neutral-300 rounded">
          <option value="">— Categoria principale (Livello 1) —</option>
          {allowedParents.map((c) => {
            const lvl = getLevel(c.id);
            return <option key={c.id} value={c.id}>{'– '.repeat(lvl)}{c.name} (L{lvl + 1})</option>;
          })}
        </select>
        {parentLevel >= 2 && <p className="text-xs text-red-600 mt-1">Livello massimo: 3</p>}
      </div>
      <Input label="Descrizione" value={data.description} onChange={(v) => setData({ ...data, description: v })} />
      <ImageUploader value={data.image_url} onChange={(v) => setData({ ...data, image_url: v })} label="Immagine categoria" />
      <Input label="Ordine" type="number" value={data.order} onChange={(v) => setData({ ...data, order: parseInt(v) || 0 })} />
      <label className="flex items-start gap-3 p-3 border border-orange-200 bg-orange-50 rounded cursor-pointer hover:bg-orange-100">
        <input
          type="checkbox"
          checked={!!data.hidden}
          onChange={(e) => setData({ ...data, hidden: e.target.checked })}
          className="mt-0.5 accent-[#d62828]"
          data-testid="category-hidden-checkbox"
        />
        <div className="flex-1">
          <div className="font-bold text-sm text-orange-900">🚧 Nascondi dal sito pubblico (Work in Progress)</div>
          <p className="text-xs text-orange-800 mt-0.5">
            Se attivo, la categoria e tutti i suoi prodotti sono invisibili ai clienti (menu, catalogo, ricerca). Rimangono comunque visibili in questo pannello admin.
          </p>
        </div>
      </label>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-5 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">Salva</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Annulla</button>
      </div>
    </form>
  );
};

// =================== PRODUCTS ===================
const ProductsTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const { toast } = useToast();

  const load = async () => {
    const url = search ? `/products?search=${encodeURIComponent(search)}` : '/products';
    const r = await api.get(url); setItems(r.data);
  };
  useEffect(() => {
    api.get('/admin/categories').then((r) => setCats(r.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  // Filter by category including all descendants
  const filteredItems = React.useMemo(() => {
    if (!categoryFilter) return items;
    const allIds = [categoryFilter, ...descendantIds(cats, categoryFilter)];
    return items.filter((p) => allIds.includes(p.category_id));
  }, [items, cats, categoryFilter]);

  const save = async (data) => {
    try {
      if (editing.id) await api.put(`/products/${editing.id}`, data);
      else await api.post('/products', data);
      setEditing(null); load();
      toast({ title: 'Salvato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    await api.delete(`/products/${id}`); load();
  };

  const duplicate = (p) => {
    // Open the ProductForm in "create" mode pre-filled with a copy of p
    // Strip id, slug, sku so the user can adjust them before saving
    setEditing({
      name: `${p.name} (copia)`,
      brand: p.brand || '',
      category_id: p.category_id || '',
      description: p.description || '',
      short_description: p.short_description || '',
      image_url: p.image_url || '',
      price_per_day: p.price_per_day || 0,
      price_per_weekend: p.price_per_weekend || null,
      sku: '', // let user set a new SKU
      available: p.available !== false,
      featured: false, // never featured by default on a copy
    });
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold">Prodotti ({items.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 max-w-[260px]">
            <option value="">Tutte le categorie</option>
            {flattenCategoriesForSelect(cats).map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca..." className="px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
          <button onClick={() => setEditing({ name: '', brand: '', category_id: '', description: '', short_description: '', image_url: '', price_per_day: 0, price_per_weekend: null, sku: '', available: true, featured: false })} className="flex items-center gap-2 px-4 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] whitespace-nowrap">
            <Plus size={16} /> Nuovo
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">Prezzo /g</th>
              <th className="px-4 py-3">Prezzo w-end</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-neutral-500">Nessun prodotto trovato.</td></tr>
            )}
            {filteredItems.map((p) => (
              <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setEditing(p)}>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-neutral-600">{p.brand}</td>
                <td className="px-4 py-3 text-neutral-600">{categoryPathFor(cats, p.category_id) || p.category_name}</td>
                <td className="px-4 py-3">€ {p.price_per_day?.toFixed(2)}</td>
                <td className="px-4 py-3 text-neutral-600">
                  {p.price_per_weekend > 0 ? (
                    <span>€ {Number(p.price_per_weekend).toFixed(2)}</span>
                  ) : (
                    <span className="text-neutral-400 italic">€ {((p.price_per_day || 0) * 1.5).toFixed(2)} auto</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {p.available ? <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">disp.</span> : <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">N/D</span>}
                  {p.featured && <span className="ml-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">★</span>}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    <Link to={`/prodotto/${p.slug}`} target="_blank" className="p-2 hover:bg-neutral-100 rounded text-neutral-600" title="Visualizza sul sito"><Eye size={16} /></Link>
                    <button onClick={() => duplicate(p)} className="p-2 hover:bg-blue-50 text-blue-700 rounded" title="Duplica prodotto" data-testid={`duplicate-product-${p.slug}`}><Copy size={16} /></button>
                    <button onClick={() => setEditing(p)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#d62828] text-white text-xs font-bold rounded hover:bg-[#b41f1f]" title="Modifica prodotto">
                      <Edit3 size={14} /> Modifica
                    </button>
                    <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" title="Elimina"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Modifica Prodotto' : 'Nuovo Prodotto'} large>
          <ProductForm initial={editing} categories={cats} onSubmit={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
};

const ProductForm = ({ initial, categories, onSubmit, onCancel }) => {
  const [d, setD] = useState({
    name: initial.name || '', slug: initial.slug || '', brand: initial.brand || '',
    category_id: initial.category_id || '', description: initial.description || '',
    short_description: initial.short_description || '', image_url: initial.image_url || '',
    price_per_day: initial.price_per_day || 0,
    price_per_weekend: initial.price_per_weekend ?? '',
    sku: initial.sku || '',
    available: initial.available !== false, featured: !!initial.featured,
  });
  const autoWeekend = ((parseFloat(d.price_per_day) || 0) * 1.5).toFixed(2);
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      const payload = { ...d };
      // Empty string / 0 / null => let backend/frontend derive it from daily * 1.5
      payload.price_per_weekend = (d.price_per_weekend === '' || d.price_per_weekend === null || parseFloat(d.price_per_weekend) === 0)
        ? null
        : parseFloat(d.price_per_weekend);
      onSubmit(payload);
    }} className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Nome *" required value={d.name} onChange={(v) => setD({ ...d, name: v })} />
        <Input label="Slug (auto)" value={d.slug} onChange={(v) => setD({ ...d, slug: v })} />
        <Input label="Brand" value={d.brand} onChange={(v) => setD({ ...d, brand: v })} />
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Categoria</label>
          <select value={d.category_id || ''} onChange={(e) => setD({ ...d, category_id: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30">
            <option value="">-- Nessuna --</option>
            {flattenCategoriesForSelect(categories).map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>
        <Input label="SKU" value={d.sku} onChange={(v) => setD({ ...d, sku: v })} />
        <Input label="Prezzo/giorno (€)" type="number" step="0.01" value={d.price_per_day} onChange={(v) => setD({ ...d, price_per_day: parseFloat(v) || 0 })} />
        <div className="md:col-span-2">
          <label className="block text-sm font-bold text-neutral-700 mb-1">Prezzo Week-end (€)</label>
          <input
            type="number"
            step="0.01"
            value={d.price_per_weekend ?? ''}
            onChange={(e) => setD({ ...d, price_per_weekend: e.target.value })}
            placeholder={`Lascia vuoto per auto: € ${autoWeekend} (giornaliero × 1,5)`}
            className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
            data-testid="product-form-price-weekend"
          />
          <p className="text-xs text-neutral-500 mt-1">Se non inserito, viene calcolato automaticamente come <strong>giornaliero × 1,5</strong> (attualmente € {autoWeekend}).</p>
        </div>
      </div>
      <Input label="Descrizione breve" value={d.short_description} onChange={(v) => setD({ ...d, short_description: v })} />
      <div>
        <label className="block text-sm font-bold text-neutral-700 mb-1">Descrizione</label>
        <textarea value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} rows={4} className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
      </div>
      <ImageUploader value={d.image_url} onChange={(v) => setD({ ...d, image_url: v })} label="Immagine prodotto" />
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.available} onChange={(e) => setD({ ...d, available: e.target.checked })} /> Disponibile</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.featured} onChange={(e) => setD({ ...d, featured: e.target.checked })} /> In Evidenza</label>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-5 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">Salva</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Annulla</button>
      </div>
    </form>
  );
};

// =================== IMPORT/EXPORT (Products) ===================
const ImportTab = () => {
  const { toast } = useToast();
  const prodRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  // Export state
  const [cats, setCats] = useState([]);
  const [selectedCatIds, setSelectedCatIds] = useState([]);
  const [exportingBusy, setExportingBusy] = useState(false);
  useEffect(() => { api.get('/admin/categories').then((r) => setCats(r.data)).catch(() => {}); }, []);

  const toggleCat = (id) => {
    setSelectedCatIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const exportAll = async () => {
    await doExport([]);
  };

  const exportSelected = async () => {
    if (selectedCatIds.length === 0) {
      toast({ title: 'Seleziona almeno una categoria', variant: 'destructive' });
      return;
    }
    await doExport(selectedCatIds);
  };

  const doExport = async (categoryIds) => {
    setExportingBusy(true);
    try {
      const r = await api.post('/admin/export/products', {
        category_ids: categoryIds,
        include_subcategories: true,
      }, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `prodotti_${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export completato', description: 'File .xlsx scaricato' });
    } catch (e) {
      toast({ title: 'Errore export', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    } finally {
      setExportingBusy(false);
    }
  };

  const exportCategoriesFile = async (categoryIds) => {
    setExportingBusy(true);
    try {
      const r = await api.post('/admin/export/categories', {
        category_ids: categoryIds,
        include_subcategories: true,
      }, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `categorie_${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export categorie completato', description: 'File .xlsx scaricato' });
    } catch (e) {
      toast({ title: 'Errore export', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    } finally {
      setExportingBusy(false);
    }
  };

  const doPreview = async (file) => {
    setBusy(true);
    setResult(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/import/products/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      setPendingFile(file);
    } catch (e) {
      toast({ title: 'Errore preview', description: e.response?.data?.detail || 'File non valido', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const r = await api.post('/admin/import/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      setPreview(null);
      setPendingFile(null);
      toast({ title: 'Import completato', description: `Importati: ${r.data.imported}, aggiornati: ${r.data.updated}` });
    } catch (e) {
      toast({ title: 'Errore import', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['name', 'slug', 'brand', 'category_path', 'description', 'short_description', 'image_url', 'price_per_day', 'price_per_weekend', 'sku', 'available', 'featured'];
    const samples = [
      ['DPA 4017', 'dpa-4017', 'DPA', 'Audio > Microfoni > Microfoni Shotgun', 'Microfono shotgun cinema', 'Shotgun robusto', 'https://example.com/p.jpg', '80.00', 'DPA-4017', 'true', 'true'],
      ['Sound Devices 888', 'sd-888', 'Sound Devices', 'Audio > Registratori Audio > Sound Devices', 'Mixer-Recorder 16 ingressi', 'Top Cinema', 'https://example.com/sd.jpg', '200.00', 'SD-888', 'true', 'true'],
      ['ARRI SkyPanel S60', 'arri-s60', 'ARRI', 'Lights > Proiettori LED', 'Pannello LED soft 450W', 'LED RGB+W cinema', 'https://example.com/arri.jpg', '180.00', 'ARRI-S60', 'true', 'false'],
    ];
    const csv = headers.join(',') + '\n' + samples.map(r => r.map(v => `"${v}"`).join(',')).join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `template-prodotti.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* ========== EXPORT CATEGORIES ========== */}
      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2"><Folder size={20} className="text-[#d62828]" /> Esporta Categorie in Excel</h2>
        <p className="text-sm text-neutral-600 mb-4">
          Scarica un file <code>.xlsx</code> con tutte le categorie e sottocategorie (nome, slug, parent_slug, descrizione, ordine).
          Utile come <strong>backup prima di un nuovo deploy</strong>: puoi reimportarlo dopo per ripristinare tutta la struttura.
        </p>

        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => exportCategoriesFile([])}
            disabled={exportingBusy}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] text-sm disabled:opacity-40"
            data-testid="export-all-categories-btn"
          >
            <Download size={14} /> Esporta tutte le categorie
          </button>
          <button
            onClick={() => exportCategoriesFile(selectedCatIds)}
            disabled={exportingBusy || selectedCatIds.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="export-selected-categories-btn"
          >
            <Download size={14} /> Esporta solo selezionate {selectedCatIds.length > 0 && `(${selectedCatIds.length})`}
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-3">
          💡 Le categorie selezionate sono quelle spuntate qui sotto nella sezione &quot;Esporta Prodotti&quot;. Le sottocategorie sono incluse automaticamente.
        </p>
      </div>

      {/* ========== EXPORT PRODUCTS ========== */}
      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2"><Download size={20} className="text-[#d62828]" /> Esporta Prodotti in Excel</h2>
        <p className="text-sm text-neutral-600 mb-4">Scarica un file <code>.xlsx</code> con tutti i prodotti, oppure seleziona una o più categorie da esportare.</p>

        <div className="mb-4">
          <label className="block text-sm font-bold text-neutral-700 mb-2">Seleziona categorie da esportare (le sottocategorie sono incluse automaticamente)</label>
          <div className="max-h-64 overflow-auto border border-neutral-200 rounded p-3 bg-neutral-50">
            {cats.length === 0 ? (
              <p className="text-sm text-neutral-500 italic">Nessuna categoria disponibile.</p>
            ) : (
              <div className="space-y-1">
                {flattenCategoriesForSelect(cats).map((opt) => (
                  <label key={opt.id} className="flex items-center gap-2 text-sm hover:bg-white px-2 py-1 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCatIds.includes(opt.id)}
                      onChange={() => toggleCat(opt.id)}
                      className="accent-[#d62828]"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {selectedCatIds.length > 0 && (
            <p className="text-xs text-neutral-500 mt-2">{selectedCatIds.length} categorie selezionate</p>
          )}
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            onClick={exportSelected}
            disabled={exportingBusy || selectedCatIds.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} /> {exportingBusy ? 'Esportazione...' : 'Esporta selezionati'}
          </button>
          <button
            onClick={exportAll}
            disabled={exportingBusy}
            className="flex items-center gap-2 px-5 py-2.5 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50 disabled:opacity-60"
          >
            <Download size={14} /> Esporta tutti i prodotti
          </button>
          {selectedCatIds.length > 0 && (
            <button onClick={() => setSelectedCatIds([])} className="px-4 py-2.5 text-sm text-neutral-600 hover:text-[#d62828]">
              Pulisci selezione
            </button>
          )}
        </div>
      </div>

      {/* ========== IMPORT ========== */}
      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2"><FileSpreadsheet size={20} className="text-[#d62828]" /> Import Prodotti da Excel</h2>
        <div className="text-sm text-neutral-600 mb-4 space-y-2">
          <p>Carica un file <code>.xlsx</code> con la <strong>prima riga</strong> contenente le intestazioni delle colonne. Le righe successive sono i prodotti da importare/aggiornare.</p>
          <p className="text-xs"><strong>Importante:</strong> le categorie devono già esistere — creale prima nella scheda "Categorie".</p>
        </div>
        <div className="bg-neutral-50 rounded p-4 mb-4 border border-neutral-200">
          <h4 className="text-sm font-bold mb-2">Colonne supportate</h4>
          <div className="grid md:grid-cols-2 gap-x-4 text-xs text-neutral-700">
            <ul className="space-y-1">
              <li><strong className="text-[#d62828]">name *</strong> — nome prodotto (obbligatorio)</li>
              <li><strong>slug</strong> — opzionale (auto-generato dal nome)</li>
              <li><strong>brand</strong> — marca</li>
              <li><strong className="text-[#d62828]">category_path</strong> — es. <code>"Audio &gt; Microfoni &gt; Shotgun"</code></li>
              <li><strong>description</strong> — descrizione completa</li>
              <li><strong>short_description</strong> — riassunto breve</li>
            </ul>
            <ul className="space-y-1">
              <li><strong>image_url</strong> — URL immagine prodotto</li>
              <li><strong>price_per_day</strong> — prezzo giornaliero (es. 80.00)</li>
              <li><strong>price_per_weekend</strong> — prezzo week-end (opz. — se vuoto = giornaliero × 1,5)</li>
              <li><strong>sku</strong> — codice articolo</li>
              <li><strong>available</strong> — true/false</li>
              <li><strong>featured</strong> — true/false (in evidenza)</li>
            </ul>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => prodRef.current?.click()} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] text-sm disabled:opacity-60">
            <Upload size={14} /> Carica .xlsx
          </button>
          <button onClick={downloadTemplate} className="flex items-center gap-2 px-5 py-2.5 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50">
            <Download size={14} /> Scarica Template CSV
          </button>
          <input ref={prodRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} />
        </div>
      </div>

      {busy && <div className="text-neutral-500">Caricamento in corso...</div>}

      {/* Preview */}
      {preview && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-bold">Anteprima ({preview.total} righe totali, mostrate prime {Math.min(preview.rows.length, 200)})</h3>
              <p className="text-xs text-neutral-500">Verifica i dati prima di confermare l'import.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setPreview(null); setPendingFile(null); }} className="px-4 py-2 border border-neutral-300 rounded text-sm font-bold">Annulla</button>
              <button onClick={confirmImport} disabled={busy} className="px-5 py-2 bg-[#d62828] text-white rounded text-sm font-bold hover:bg-[#b41f1f] disabled:opacity-60">
                Conferma e Importa
              </button>
            </div>
          </div>
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full text-xs">
              <thead className="bg-neutral-100 sticky top-0">
                <tr>
                  {preview.headers.map((h) => <th key={h} className="px-3 py-2 text-left font-bold border-b">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={`${row.name || row.slug || 'row'}-${i}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                    {preview.headers.map((h) => (
                      <td key={h} className="px-3 py-2 truncate max-w-[200px]" title={row[h]}>{row[h] || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="p-5 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-bold text-green-800 mb-2">Risultato Import</h3>
          <ul className="text-sm text-green-900 space-y-1">
            <li>✓ Importati: <strong>{result.imported}</strong></li>
            <li>✓ Aggiornati: <strong>{result.updated}</strong></li>
            <li>⚠ Errori: <strong>{result.errors?.length || 0}</strong></li>
          </ul>
          {result.errors?.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-bold">Dettagli errori</summary>
              <pre className="mt-2 p-2 bg-white rounded overflow-auto max-h-40">{JSON.stringify(result.errors, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// =================== CART QUOTES ===================
const CartQuotesTab = () => {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get('/cart-quotes'); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm('Eliminare?')) { await api.delete(`/cart-quotes/${id}`); load(); } };
  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Richieste Carrello ({items.length})</h2>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-neutral-500">Nessuna richiesta dal carrello.</p>}
        {items.map((q) => (
          <div key={q.id} className="p-4 bg-white border border-neutral-200 rounded-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="font-bold text-neutral-900">
                  {q.name}
                  <span className="text-sm text-neutral-500 font-normal ml-2">· {q.email}</span>
                  {q.customer_id && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">Cliente registrato</span>}
                </div>
                {q.company && <div className="text-sm text-neutral-600">{q.company}</div>}
                {q.phone && <div className="text-sm text-neutral-600">☎ {q.phone}</div>}
                {(q.start_date || q.end_date) && <div className="text-sm text-neutral-600 mt-1">📅 Dal {q.start_date || '?'} al {q.end_date || '?'}</div>}
                <div className="mt-3 bg-neutral-50 p-3 rounded border border-neutral-100">
                  <p className="text-xs font-bold text-neutral-700 mb-2">Articoli richiesti:</p>
                  <ul className="text-sm space-y-1">
                    {q.items.map((it, i) => (
                      <li key={it.product_id || it.product_slug || `${q.id}-item-${i}`} className="flex items-center justify-between">
                        <span><strong>{it.quantity}×</strong> {it.product_name} {it.brand && <span className="text-xs text-neutral-500">({it.brand})</span>}</span>
                        {it.price_per_day > 0 && <span className="text-xs text-neutral-500">€{it.price_per_day.toFixed(2)}/g listino</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-2 flex gap-3 flex-wrap text-xs">
                  {q.delivery_pickup && <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded font-bold">🚚 Consegna/Ritiro: {q.delivery_address || 'indirizzo da concordare'}</span>}
                  {q.setup_teardown && <span className="bg-purple-50 text-purple-800 px-2 py-1 rounded font-bold">🔧 Setup/Smontaggio {q.setup_location && `: ${q.setup_location}`}</span>}
                </div>
                {q.message && <div className="text-sm text-neutral-700 mt-2 italic whitespace-pre-wrap">"{q.message}"</div>}
                <div className="text-xs text-neutral-400 mt-2">{new Date(q.created_at).toLocaleString('it-IT')}</div>
              </div>
              <button onClick={() => del(q.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== QUOTES & CONTACTS ===================
const QuotesTab = () => {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get('/quotes'); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm('Eliminare?')) { await api.delete(`/quotes/${id}`); load(); } };
  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Richieste Preventivo ({items.length})</h2>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-neutral-500">Nessuna richiesta.</p>}
        {items.map((q) => (
          <div key={q.id} className="p-4 bg-white border border-neutral-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold text-neutral-900">{q.name} <span className="text-sm text-neutral-500 font-normal">· {q.email}</span></div>
                {q.product_name && <div className="text-sm text-[#d62828] font-medium mt-1">{q.product_name}</div>}
                {q.company && <div className="text-sm text-neutral-600">{q.company}</div>}
                {q.phone && <div className="text-sm text-neutral-600">☎ {q.phone}</div>}
                {(q.start_date || q.end_date) && <div className="text-sm text-neutral-600 mt-1">Dal {q.start_date || '?'} al {q.end_date || '?'}</div>}
                {q.message && <div className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap">{q.message}</div>}
                <div className="text-xs text-neutral-400 mt-2">{new Date(q.created_at).toLocaleString('it-IT')}</div>
              </div>
              <button onClick={() => del(q.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContactsTab = () => {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get('/contacts'); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm('Eliminare?')) { await api.delete(`/contacts/${id}`); load(); } };
  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Messaggi Contatto ({items.length})</h2>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-neutral-500">Nessun messaggio.</p>}
        {items.map((c) => (
          <div key={c.id} className="p-4 bg-white border border-neutral-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold text-neutral-900">{c.name} <span className="text-sm text-neutral-500 font-normal">· {c.email}</span></div>
                {c.subject && <div className="text-sm font-medium text-[#d62828] mt-1">{c.subject}</div>}
                {c.phone && <div className="text-sm text-neutral-600">☎ {c.phone}</div>}
                <div className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap">{c.message}</div>
                <div className="text-xs text-neutral-400 mt-2">{new Date(c.created_at).toLocaleString('it-IT')}</div>
              </div>
              <button onClick={() => del(c.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== Helpers ===================
const Input = ({ label, value, onChange, type = 'text', step, required }) => (
  <div>
    <label className="block text-sm font-bold text-neutral-700 mb-1">{label}</label>
    <input type={type} step={step} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 focus:border-[#d62828]" />
  </div>
);

const Modal = ({ children, onClose, title, large }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Only close when user clicks/mousedowns DIRECTLY on the backdrop (not on a
  // child that bubbles up). Using onMouseDown avoids the modal closing when the
  // user selects text in an input/textarea and releases the mouse outside the
  // dialog: with onClick the "click" fires on the common ancestor and closes the modal.
  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onMouseDown={handleBackdropMouseDown}
      data-testid="modal-backdrop"
    >
      <div className={`bg-white rounded-xl ${large ? 'max-w-3xl' : 'max-w-md'} w-full max-h-[90vh] overflow-y-auto`} data-testid="modal-content">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded" data-testid="modal-close-btn"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// Flatten categories for select dropdown with hierarchy indentation
function flattenCategoriesForSelect(categories) {
  if (!categories || categories.length === 0) return [];
  const out = [];
  const walk = (parentId, depth) => {
    const children = categories.filter((c) => (c.parent_id || null) === parentId);
    for (const c of children) {
      out.push({ id: c.id, label: `${'— '.repeat(depth)}${c.name}` });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// Get descendant ids of a category
function descendantIds(categories, parentId) {
  const out = [];
  const stack = [parentId];
  while (stack.length) {
    const cur = stack.pop();
    const children = categories.filter((c) => c.parent_id === cur);
    for (const c of children) {
      out.push(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

// Get full category path "A > B > C"
function categoryPathFor(categories, catId) {
  if (!catId) return '';
  const cat = categories.find((c) => c.id === catId);
  if (!cat) return '';
  const parts = [cat.name];
  let cur = cat;
  while (cur.parent_id) {
    const p = categories.find((c) => c.id === cur.parent_id);
    if (!p) break;
    parts.unshift(p.name);
    cur = p;
  }
  return parts.join(' > ');
}

// =================== OPENING HOURS ===================
const OpeningHoursTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/opening-hours')
      .then((r) => setData(r.data))
      .catch(() => setData({ days: [], on_call_phone: '', on_call_note: '', delivery_note: '' }));
  }, []);

  if (!data) return <div className="text-neutral-500">Caricamento...</div>;

  const updateDay = (idx, field, value) => {
    const days = [...data.days];
    days[idx] = { ...days[idx], [field]: value };
    setData({ ...data, days });
  };

  const addDay = () => {
    setData({ ...data, days: [...data.days, { label: '', hours: '' }] });
  };

  const removeDay = (idx) => {
    setData({ ...data, days: data.days.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/opening-hours', data);
      toast({ title: 'Orari salvati', description: 'Le modifiche sono visibili sul sito.' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore salvataggio', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Ripristinare gli orari predefiniti?')) return;
    const defaults = {
      days: [
        { label: 'Lunedì', hours: '09:00 – 13:00 / 14:00 – 18:00' },
        { label: 'Martedì', hours: '09:00 – 13:00 / 14:00 – 18:00' },
        { label: 'Mercoledì', hours: '09:00 – 13:00 / 14:00 – 18:00' },
        { label: 'Giovedì', hours: '09:00 – 13:00 / 14:00 – 18:00' },
        { label: 'Venerdì', hours: '09:00 – 13:00 / 14:00 – 17:00' },
        { label: 'Sabato', hours: 'Su appuntamento' },
        { label: 'Domenica', hours: 'Su appuntamento' },
      ],
      on_call_phone: '+39 339 2020405',
      on_call_note: 'Per produzioni in corso è attiva la reperibilità telefonica.',
      delivery_note: 'Servizio di consegna e ritiro disponibile su richiesta in tutta Italia.',
    };
    setData(defaults);
  };

  return (
    <div className="space-y-6" data-testid="opening-hours-tab">
      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Orari di Apertura</h2>
          <button onClick={reset} className="text-xs text-neutral-500 hover:text-[#d62828] flex items-center gap-1" data-testid="reset-hours-btn">
            <RotateCcw size={12} /> Ripristina default
          </button>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Modifica giorni e orari mostrati nella pagina pubblica <strong>Informazioni → Orario apertura</strong>.
        </p>

        <div className="space-y-2 mb-4">
          {data.days.map((d, i) => (
            <div key={i} className="grid grid-cols-[160px_1fr_auto] gap-2 items-center" data-testid={`admin-day-row-${i}`}>
              <input
                value={d.label}
                onChange={(e) => updateDay(i, 'label', e.target.value)}
                placeholder="Giorno (es. Lunedì)"
                className="px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
                data-testid={`admin-day-label-${i}`}
              />
              <input
                value={d.hours}
                onChange={(e) => updateDay(i, 'hours', e.target.value)}
                placeholder="Orari (es. 09:00 – 18:00 / Chiuso / Su appuntamento)"
                className="px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
                data-testid={`admin-day-hours-${i}`}
              />
              <button
                onClick={() => removeDay(i)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
                title="Rimuovi riga"
                data-testid={`admin-day-remove-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addDay}
          className="flex items-center gap-1 text-sm text-[#d62828] font-bold hover:underline"
          data-testid="add-day-btn"
        >
          <Plus size={14} /> Aggiungi giorno
        </button>
      </div>

      <div className="p-6 bg-white border border-neutral-200 rounded-lg space-y-3">
        <h3 className="font-bold">Reperibilità e note aggiuntive</h3>
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Telefono reperibilità</label>
          <input
            value={data.on_call_phone}
            onChange={(e) => setData({ ...data, on_call_phone: e.target.value })}
            placeholder="+39 339 2020405"
            className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
            data-testid="admin-oncall-phone"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Nota reperibilità</label>
          <textarea
            value={data.on_call_note}
            onChange={(e) => setData({ ...data, on_call_note: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
            data-testid="admin-oncall-note"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Nota consegna/ritiro</label>
          <textarea
            value={data.delivery_note}
            onChange={(e) => setData({ ...data, delivery_note: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
            data-testid="admin-delivery-note"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60 text-sm"
          data-testid="save-hours-btn"
        >
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </button>
      </div>
    </div>
  );
};

// =================== CUSTOMERS (registered fixed customers) ===================
const CustomersTab = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // customer being edited
  const [viewingHistory, setViewingHistory] = useState(null); // customer whose history we're viewing

  const load = async () => {
    const url = search ? `/admin/customers?search=${encodeURIComponent(search)}` : '/admin/customers';
    const r = await api.get(url);
    setItems(r.data);
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  const save = async (customerId, data) => {
    try {
      await api.put(`/admin/customers/${customerId}`, data);
      setEditing(null);
      load();
      toast({ title: 'Cliente aggiornato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Eliminare il cliente ${email}?\nI suoi preventivi passati rimarranno nel database ma il cliente non potrà più accedere.`)) return;
    try {
      await api.delete(`/admin/customers/${id}`);
      load();
      toast({ title: 'Cliente eliminato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold">Clienti Registrati ({items.length})</h2>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, email o azienda..."
          className="px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 md:min-w-[320px]"
          data-testid="customer-search"
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Azienda</th>
              <th className="px-4 py-3">Telefono</th>
              <th className="px-4 py-3 text-center">Sconto</th>
              <th className="px-4 py-3 text-center">Richieste</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-neutral-500">Nessun cliente registrato trovato.</td></tr>
            )}
            {items.map((c) => {
              const hasCatDiscount = Object.keys(c.discount_by_category || {}).length > 0;
              return (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{c.email}</td>
                  <td className="px-4 py-3 text-neutral-600">{c.company || '—'}</td>
                  <td className="px-4 py-3 text-neutral-600">{c.phone || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {(c.discount_all > 0 || hasCatDiscount) ? (
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {c.discount_all > 0 && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold">−{c.discount_all}% tutto</span>}
                        {hasCatDiscount && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">{Object.keys(c.discount_by_category).length} cat</span>}
                      </div>
                    ) : <span className="text-xs text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setViewingHistory(c)} className="text-xs font-bold text-[#d62828] hover:underline" data-testid={`view-history-${c.id}`}>
                      {c.quote_count || 0} storico
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing(c)} className="flex items-center gap-1 px-3 py-1.5 bg-[#d62828] text-white text-xs font-bold rounded hover:bg-[#b41f1f]" data-testid={`edit-customer-${c.id}`}>
                        <Edit3 size={14} /> Modifica
                      </button>
                      <button onClick={() => del(c.id, c.email)} className="p-2 hover:bg-red-50 text-red-600 rounded" title="Elimina"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Modifica cliente: ${editing.name}`} large>
          <CustomerForm initial={editing} onSubmit={(data) => save(editing.id, data)} onCancel={() => setEditing(null)} />
        </Modal>
      )}

      {viewingHistory && (
        <Modal onClose={() => setViewingHistory(null)} title={`Storico richieste: ${viewingHistory.name}`} large>
          <CustomerHistory customerId={viewingHistory.id} />
        </Modal>
      )}
    </div>
  );
};

const CustomerForm = ({ initial, onSubmit, onCancel }) => {
  const [d, setD] = useState({
    name: initial.name || '',
    phone: initial.phone || '',
    company: initial.company || '',
    discount_all: initial.discount_all || 0,
    discount_by_category: { ...(initial.discount_by_category || {}) },
    notes: initial.notes || '',
  });
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get('/admin/categories').then((r) => setCats(r.data)).catch(() => {}); }, []);

  const setCatDiscount = (catId, val) => {
    const dd = { ...d.discount_by_category };
    const num = parseFloat(val);
    if (!val || isNaN(num) || num <= 0) delete dd[catId];
    else dd[catId] = Math.min(100, Math.max(0, num));
    setD({ ...d, discount_by_category: dd });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(d); }} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Nome" value={d.name} onChange={(v) => setD({ ...d, name: v })} />
        <Input label="Telefono" value={d.phone} onChange={(v) => setD({ ...d, phone: v })} />
        <Input label="Azienda" value={d.company} onChange={(v) => setD({ ...d, company: v })} />
        <div>
          <label className="block text-sm font-bold text-neutral-700 mb-1">Email (non modificabile)</label>
          <input value={initial.email} disabled className="w-full px-3 py-2 border border-neutral-200 bg-neutral-50 rounded text-neutral-500" />
        </div>
      </div>

      <div className="p-4 bg-green-50 border border-green-200 rounded">
        <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">💰 Sconto Globale</h4>
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={d.discount_all}
            onChange={(e) => setD({ ...d, discount_all: parseFloat(e.target.value) || 0 })}
            className="w-24 px-3 py-2 border border-green-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30"
            data-testid="discount-all-input"
          />
          <span className="text-green-800 font-bold">%</span>
          <p className="text-xs text-green-800 ml-2">Applicato a tutti i prodotti quando il cliente è loggato. 0 = nessuno sconto globale.</p>
        </div>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded">
        <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">🏷 Sconti per Categoria</h4>
        <p className="text-xs text-blue-800 mb-3">Se una categoria ha uno sconto specifico, prevale sullo sconto globale per i prodotti di quella categoria.</p>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {flattenCategoriesForSelect(cats).map((opt) => (
            <div key={opt.id} className="flex items-center gap-2 py-1">
              <label className="flex-1 text-sm text-neutral-700 truncate">{opt.label}</label>
              <input
                type="number"
                step="0.5"
                min="0"
                max="100"
                placeholder="0"
                value={d.discount_by_category[opt.id] || ''}
                onChange={(e) => setCatDiscount(opt.id, e.target.value)}
                className="w-20 px-2 py-1 border border-blue-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                data-testid={`discount-cat-${opt.id}`}
              />
              <span className="text-blue-800 text-sm">%</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-neutral-700 mb-1">Note admin (private, non visibili al cliente)</label>
        <textarea
          value={d.notes}
          onChange={(e) => setD({ ...d, notes: e.target.value })}
          rows={2}
          placeholder="Es. cliente fisso, condizioni speciali, ecc."
          className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-5 py-2 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]" data-testid="save-customer-btn">Salva</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Annulla</button>
      </div>
    </form>
  );
};

const CustomerHistory = ({ customerId }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/customers/${customerId}/quotes`)
      .then((r) => setQuotes(r.data))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="text-neutral-500">Caricamento...</div>;
  if (quotes.length === 0) return <p className="text-neutral-500 py-4">Nessuna richiesta ancora inviata da questo cliente.</p>;

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
      {quotes.map((q) => (
        <div key={q.id} className="p-4 border border-neutral-200 rounded-lg">
          <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="font-bold text-sm">{q.items?.length || 0} prodotti richiesti</div>
              <div className="text-xs text-neutral-500">{new Date(q.created_at).toLocaleString('it-IT')}</div>
              {(q.start_date || q.end_date) && (
                <div className="text-xs text-neutral-600 mt-0.5">📅 {q.start_date || '?'} → {q.end_date || '?'}</div>
              )}
            </div>
            {q.delivery_pickup && <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">CONSEGNA</span>}
            {q.setup_teardown && <span className="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-bold">MONTAGGIO</span>}
          </div>
          <div className="text-xs text-neutral-600 space-y-0.5 mb-2">
            {q.delivery_address && <div>Consegna: {q.delivery_address}</div>}
            {q.setup_location && <div>Montaggio: {q.setup_location}</div>}
          </div>
          <div className="text-xs">
            <details>
              <summary className="cursor-pointer text-[#d62828] font-bold">Vedi prodotti ({q.items?.length || 0})</summary>
              <ul className="mt-2 space-y-1">
                {(q.items || []).map((it, i) => (
                  <li key={it.product_id || it.product_slug || `${q.id}-hist-${i}`} className="flex justify-between border-b border-neutral-100 pb-1">
                    <span className="text-neutral-700">{it.product_name} {it.brand && <span className="text-neutral-400">· {it.brand}</span>}</span>
                    <span className="font-mono text-neutral-500">×{it.quantity}</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
          {q.message && <p className="text-xs italic text-neutral-500 mt-2 border-t border-neutral-100 pt-2">"{q.message}"</p>}
        </div>
      ))}
    </div>
  );
};

// =================== SITE INFO (Contatti + Dati Fiscali) ===================
const SiteInfoTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/site-info')
      .then((r) => setData(r.data))
      .catch(() => toast({ title: 'Errore', description: 'Impossibile caricare i dati.', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading || !data) return <div className="text-neutral-500">Caricamento...</div>;

  const updateContact = (field, value) => setData({ ...data, contact: { ...data.contact, [field]: value } });
  const updateFiscal = (field, value) => setData({ ...data, fiscal: { ...data.fiscal, [field]: value } });

  const save = async () => {
    setSaving(true);
    try {
      // Clean list fields: split by newline and trim
      const cleanList = (arr) => (Array.isArray(arr) ? arr : String(arr || '').split('\n')).map((s) => s.trim()).filter(Boolean);
      const payload = {
        contact: {
          address_lines: cleanList(data.contact.address_lines),
          phones: cleanList(data.contact.phones),
          emails: cleanList(data.contact.emails),
          hours_lines: cleanList(data.contact.hours_lines),
          intro: data.contact.intro || '',
        },
        fiscal: data.fiscal,
      };
      const r = await api.put('/admin/site-info', payload);
      setData(r.data);
      toast({ title: 'Salvato', description: 'Le modifiche sono ora visibili sul sito.' });
    } catch (e) {
      toast({ title: 'Errore', description: e.response?.data?.detail || 'Salvataggio fallito.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const asMultiline = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));

  return (
    <div className="space-y-6" data-testid="site-info-tab">
      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={20} className="text-[#d62828]" />
          <h2 className="text-xl font-bold">Pagina Contatti</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">Ogni riga in una sola riga di testo (usa Invio per aggiungere una nuova riga alla lista).</p>
        <div className="grid md:grid-cols-2 gap-4">
          <MultilineField label="Indirizzo (una riga per ciascuna)" value={asMultiline(data.contact.address_lines)} onChange={(v) => updateContact('address_lines', v.split('\n'))} testid="site-address" />
          <MultilineField label="Telefoni (uno per riga)" value={asMultiline(data.contact.phones)} onChange={(v) => updateContact('phones', v.split('\n'))} testid="site-phones" />
          <MultilineField label="Email (una per riga)" value={asMultiline(data.contact.emails)} onChange={(v) => updateContact('emails', v.split('\n'))} testid="site-emails" />
          <MultilineField label="Orario (una riga per ciascuna)" value={asMultiline(data.contact.hours_lines)} onChange={(v) => updateContact('hours_lines', v.split('\n'))} testid="site-hours" />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-bold text-neutral-700 mb-1">Testo introduttivo pagina contatti</label>
          <textarea
            value={data.contact.intro || ''}
            onChange={(e) => updateContact('intro', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
            data-testid="site-intro"
          />
        </div>
      </div>

      <div className="p-6 bg-white border border-neutral-200 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={20} className="text-[#d62828]" />
          <h2 className="text-xl font-bold">Dati Fiscali</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Ragione Sociale" value={data.fiscal.ragione_sociale} onChange={(v) => updateFiscal('ragione_sociale', v)} />
          <Input label="Sede Legale" value={data.fiscal.sede_legale} onChange={(v) => updateFiscal('sede_legale', v)} />
          <Input label="P. IVA / C.F." value={data.fiscal.piva_cf} onChange={(v) => updateFiscal('piva_cf', v)} />
          <Input label="Capitale Sociale" value={data.fiscal.cap_soc} onChange={(v) => updateFiscal('cap_soc', v)} />
          <Input label="Codice SDI" value={data.fiscal.codice_sdi} onChange={(v) => updateFiscal('codice_sdi', v)} />
          <Input label="PEC" value={data.fiscal.pec} onChange={(v) => updateFiscal('pec', v)} />
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="site-info-save"
          className="px-6 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60"
        >
          {saving ? 'Salvataggio...' : 'Salva Modifiche'}
        </button>
      </div>
    </div>
  );
};

const MultilineField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm font-bold text-neutral-700 mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      data-testid={testid}
      className="w-full px-3 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 font-mono text-sm"
    />
  </div>
);

export default AdminDashboard;
